'use client';

/**
 * デバッグIssue報告モーダル（簡素化版）
 *
 * 2ステップフロー:
 * 1. 簡易入力（必須: 何が嫌だったか、オプション: 期待動作・再現手順・環境情報）
 * 2. AI 整形プレビュー（編集可能）→ 投稿
 *
 * フォールバック: AI 整形失敗時は元の入力で直接投稿可能
 */

import { ChevronDown, ExternalLink, Loader2, RotateCcw, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  type AutoCollectedData,
  captureScreenshot,
  collectDebugData,
  formatSimplifiedIssueBody,
  generateGitHubIssueUrl,
  type ScreenshotData,
  uploadScreenshot,
} from '@/lib/debug';
import { logger } from '@/lib/logger';

import { AiFormattedPreview } from './AiFormattedPreview';
import { useAiFormat } from './useAiFormat';

interface DebugIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  preloadedScreenshot?: ScreenshotData | null;
}

const GITHUB_OWNER = 'Atsumell';
const GITHUB_REPO = 'kakusill';

type ModalStep = 'input' | 'preview';

/** ブラウザ環境情報を自動取得 */
function detectEnvironmentInfo(): string {
  if (typeof navigator === 'undefined') return '';
  const ua = navigator.userAgent;
  let browser = 'Other';
  if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari')) browser = 'Safari';
  const os = navigator.platform;
  const screenSize = `${window.screen.width}x${window.screen.height}`;
  return `${browser} / ${os} / ${screenSize}`;
}

/** スクリーンショットをアップロードし、成功時は URL を返す */
async function tryUploadScreenshot(screenshot: ScreenshotData | null): Promise<string | undefined> {
  if (!screenshot) return undefined;
  const result = await uploadScreenshot(screenshot.dataUrl);
  return result.success && result.url ? result.url : undefined;
}

/** 別タブで URL を開く */
function safeOpen(url: string): boolean {
  return !!window.open(url, '_blank', 'noopener,noreferrer');
}

interface ApiResult {
  success?: boolean;
  issueUrl?: string;
  fallbackToUrl?: boolean;
  error?: string;
}

function isApiResult(raw: unknown): raw is ApiResult {
  if (!raw || typeof raw !== 'object') return false;
  const obj = raw as Record<string, unknown>;
  return (
    (obj.success === undefined || typeof obj.success === 'boolean') &&
    (obj.issueUrl === undefined || typeof obj.issueUrl === 'string') &&
    (obj.fallbackToUrl === undefined || typeof obj.fallbackToUrl === 'boolean') &&
    (obj.error === undefined || typeof obj.error === 'string')
  );
}

/** API レスポンスを解析 */
function parseApiResult(raw: unknown): ApiResult | null {
  return isApiResult(raw) ? raw : null;
}

const FOOTER_MARKER_PLAIN = '---\n*Generated by Debug Issue Tool*';
const FOOTER_MARKER_AI = '---\n*Generated by Debug Issue Tool (AI formatted)*';

/** フッターマーカーの前にスクリーンショットセクションを挿入 */
function insertScreenshotBeforeFooter(body: string, uploadedUrl: string): string {
  const screenshotSection = `### スクリーンショット\n\n![screenshot](${uploadedUrl})\n\n`;
  if (body.includes(FOOTER_MARKER_AI)) {
    return body.replace(FOOTER_MARKER_AI, `${screenshotSection}${FOOTER_MARKER_AI}`);
  }
  if (body.includes(FOOTER_MARKER_PLAIN)) {
    return body.replace(FOOTER_MARKER_PLAIN, `${screenshotSection}${FOOTER_MARKER_PLAIN}`);
  }
  return `${body}\n\n${screenshotSection}`;
}

type SubmitIssueResult =
  | { success: true }
  | { success: false; error: string; issueCreated?: boolean; issueUrl?: string };

/** スクリーンショット付きで Issue を投稿する統合関数 */
async function submitIssue(
  screenshot: ScreenshotData | null,
  buildFormattedBody: () => { title: string; body: string },
): Promise<SubmitIssueResult> {
  try {
    const uploadedUrl = await tryUploadScreenshot(screenshot);
    const { title, body } = buildFormattedBody();

    const finalBody = uploadedUrl ? insertScreenshotBeforeFooter(body, uploadedUrl) : body;

    return createIssueViaApi(title, finalBody);
  } catch (err) {
    logger.error('Failed to create issue:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Issue 作成に失敗しました' };
  }
}

/** API 経由で Issue を作成し、成功時は新しいタブで開く */
async function createIssueViaApi(title: string, body: string): Promise<SubmitIssueResult> {
  const apiResponse = await fetch('/api/debug/issue', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, body, labels: ['waiting-for-triage'] }),
  });

  const apiResult = parseApiResult(await apiResponse.json().catch(() => null));
  const popupError = 'ポップアップがブロックされました。ブラウザの設定を確認してください。';

  if (apiResponse.ok && apiResult?.success && typeof apiResult.issueUrl === 'string') {
    if (!safeOpen(apiResult.issueUrl)) {
      return {
        success: false,
        issueCreated: true,
        issueUrl: apiResult.issueUrl,
        error: `Issue は作成されましたが、ポップアップがブロックされました。こちらから確認できます: ${apiResult.issueUrl}`,
      };
    }
    return { success: true };
  }

  if (apiResult?.fallbackToUrl || apiResponse.status === 501) {
    const issueUrl = generateGitHubIssueUrl(GITHUB_OWNER, GITHUB_REPO, title, body);
    return safeOpen(issueUrl) ? { success: true } : { success: false, error: popupError };
  }

  return { success: false, error: apiResult?.error || 'Issue 作成に失敗しました' };
}

export function DebugIssueModal({ isOpen, onClose, preloadedScreenshot }: DebugIssueModalProps) {
  // フォーム入力状態
  const [complaint, setComplaint] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [environmentInfo, setEnvironmentInfo] = useState('');
  const [showOptional, setShowOptional] = useState(false);
  const [includeAutoData, setIncludeAutoData] = useState(true);

  // モーダル状態
  const [step, setStep] = useState<ModalStep>('input');
  const [isCreating, setIsCreating] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [issueAlreadyCreated, setIssueAlreadyCreated] = useState(false);

  // デバッグデータ
  const [autoCollected, setAutoCollected] = useState<AutoCollectedData | null>(null);
  const [screenshot, setScreenshot] = useState<ScreenshotData | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);

  // 二重送信防止
  const isSubmittingRef = useRef(false);

  // AI 整形フック
  const aiFormat = useAiFormat();

  // モーダルオープン時にデバッグデータ収集
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    collectDebugData()
      .then((data) => {
        if (!cancelled) setAutoCollected(data);
      })
      .catch((err) => {
        logger.warn('Failed to collect debug data:', err);
      });

    if (preloadedScreenshot) {
      setScreenshot(preloadedScreenshot);
    }

    setEnvironmentInfo(detectEnvironmentInfo());

    return () => {
      cancelled = true;
    };
  }, [isOpen, preloadedScreenshot]);

  const resetForm = useCallback(() => {
    setComplaint('');
    setExpectedBehavior('');
    setStepsToReproduce('');
    setEnvironmentInfo('');
    setShowOptional(false);
    setIncludeAutoData(true);
    setStep('input');
    setIsCreating(false);
    setSubmitError(null);
    setIssueAlreadyCreated(false);
    setAutoCollected(null);
    setScreenshot(null);
    setScreenshotError(null);
    aiFormat.reset();
    isSubmittingRef.current = false;
  }, [aiFormat]);

  const handleClose = useCallback(() => {
    if (aiFormat.status === 'formatting') return;
    resetForm();
    onClose();
  }, [aiFormat.status, onClose, resetForm]);

  // ステップ1: AI 整形をリクエスト（スクショアップロードと並列実行）
  const handleRequestFormat = useCallback(async () => {
    if (!complaint.trim()) {
      setSubmitError('何が嫌だったかを入力してください');
      return;
    }
    setSubmitError(null);

    const input = {
      complaint: complaint.trim(),
      expectedBehavior: expectedBehavior.trim() || undefined,
      stepsToReproduce: stepsToReproduce.trim() || undefined,
      environmentInfo: environmentInfo.trim() || undefined,
    };

    // スクショアップロードと AI 整形を並列実行
    const uploadPromise =
      includeAutoData && screenshot
        ? tryUploadScreenshot(screenshot).then((url) => {
            if (url) setScreenshot((prev) => (prev ? { ...prev, uploadedUrl: url } : prev));
          })
        : Promise.resolve();

    await Promise.all([aiFormat.requestFormat(input), uploadPromise]);
    // AI 整形が失敗した場合もプレビュー画面でエラー表示・リトライ UI を提供する
    setStep('preview');
  }, [complaint, expectedBehavior, stepsToReproduce, environmentInfo, aiFormat, includeAutoData, screenshot]);

  // 整形結果から Markdown を組み立て
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Existing debug issue formatter flow is outside the invitation accept fix scope.
  const buildFormattedBody = useCallback((): { title: string; body: string } => {
    if (!aiFormat.result) {
      return {
        title: complaint.trim().slice(0, 50),
        body: formatSimplifiedIssueBody({
          complaint,
          expectedBehavior,
          stepsToReproduce,
          environmentInfo,
          autoCollected: includeAutoData ? (autoCollected ?? undefined) : undefined,
        }),
      };
    }

    const sections = aiFormat.result.sections.map((s) => `## ${s.heading}\n\n${s.content}`).join('\n\n');

    // autoCollected を AI 成功パスでも含める
    let autoCollectedText = '';
    if (includeAutoData && autoCollected) {
      const ac = autoCollected;
      const timestamp = new Date(ac.timestamp);
      const formattedDate = timestamp.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'Asia/Tokyo',
      });
      const basicInfo = `| 項目 | 値 |\n|-----|-----|\n| 発生日時 | ${formattedDate} JST |\n| URL | ${ac.url} |\n| ブラウザ | ${ac.browser} |\n| 画面サイズ | ${ac.screenSize.width} x ${ac.screenSize.height} |`;

      // アクション履歴
      let chatHistoryText = '';
      if (ac.chatHistory.length > 0) {
        const historyLines = ac.chatHistory
          .map((msg) => {
            const time = new Date(msg.timestamp).toLocaleTimeString('ja-JP', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            });
            const role = msg.role === 'user' ? 'User' : 'Assistant';
            const content = msg.content.length > 500 ? `${msg.content.slice(0, 500)}...(省略)` : msg.content;
            return `[${time}] ${role}: ${content}`;
          })
          .join('\n');
        chatHistoryText = `\n\n### アクション履歴\n\n<details>\n<summary>クリックして展開（${ac.chatHistory.length}件）</summary>\n\n\`\`\`\n${historyLines}\n\`\`\`\n\n</details>`;
      }

      // コンソールエラー
      let consoleErrorsText = '';
      if (ac.consoleErrors.length > 0) {
        const errorLines = ac.consoleErrors
          .map((err) => {
            const time = new Date(err.timestamp).toLocaleTimeString('ja-JP', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            });
            return `[${time}] ${err.level.toUpperCase()}: ${err.message}`;
          })
          .join('\n');
        consoleErrorsText = `\n\n### コンソールエラー（直近）\n\n<details>\n<summary>クリックして展開（${ac.consoleErrors.length}件）</summary>\n\n\`\`\`\n${errorLines}\n\`\`\`\n\n</details>`;
      }

      autoCollectedText = `\n\n---\n\n## 自動収集データ\n\n> 以下はデバッグツールにより自動収集されました\n\n${basicInfo}${chatHistoryText}${consoleErrorsText}`;
    }

    return {
      title: aiFormat.result.title,
      body: `${sections}${autoCollectedText}\n\n---\n*Generated by Debug Issue Tool (AI formatted)*`,
    };
  }, [aiFormat.result, complaint, expectedBehavior, stepsToReproduce, environmentInfo, includeAutoData, autoCollected]);

  // Issue を投稿
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Existing debug issue submission flow is outside the invitation accept fix scope.
  const handleSubmit = useCallback(async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsCreating(true);
    setSubmitError(null);

    const submitResult = await submitIssue(includeAutoData ? screenshot : null, buildFormattedBody);
    if (submitResult.success) {
      handleClose();
    } else {
      setSubmitError(submitResult.error ?? 'Issue 作成に失敗しました');
      if (submitResult.issueCreated) setIssueAlreadyCreated(true);
    }

    setIsCreating(false);
    isSubmittingRef.current = false;
  }, [screenshot, includeAutoData, buildFormattedBody, handleClose]);

  // 整形せずに投稿（aiFormat.result の状態に依存せず直接フォールバックボディを構築）
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Existing fallback submission flow is outside the invitation accept fix scope.
  const handleSubmitWithoutFormat = useCallback(async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsCreating(true);
    setSubmitError(null);

    const buildFallbackBody = () => ({
      title: complaint.trim().slice(0, 50),
      body: formatSimplifiedIssueBody({
        complaint,
        expectedBehavior,
        stepsToReproduce,
        environmentInfo,
        autoCollected: includeAutoData ? (autoCollected ?? undefined) : undefined,
      }),
    });

    const submitResult = await submitIssue(includeAutoData ? screenshot : null, buildFallbackBody);
    if (submitResult.success) {
      handleClose();
    } else {
      setSubmitError(submitResult.error ?? 'Issue 作成に失敗しました');
      if (submitResult.issueCreated) setIssueAlreadyCreated(true);
    }

    setIsCreating(false);
    isSubmittingRef.current = false;
  }, [
    complaint,
    expectedBehavior,
    stepsToReproduce,
    environmentInfo,
    includeAutoData,
    autoCollected,
    screenshot,
    handleClose,
  ]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isCreating && !isSubmittingRef.current && aiFormat.status !== 'formatting') {
          handleClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{step === 'input' ? 'Issue 報告' : 'AI 整形結果の確認'}</DialogTitle>
          <DialogDescription>
            {step === 'input'
              ? '発生した問題と期待する動作を入力し、必要に応じて自動収集データを添付します。'
              : 'AI が整形した Issue 内容を確認して投稿します。'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-4">
          <div className="space-y-4 pb-4">
            {step === 'input' ? (
              <InputStep
                complaint={complaint}
                expectedBehavior={expectedBehavior}
                stepsToReproduce={stepsToReproduce}
                environmentInfo={environmentInfo}
                showOptional={showOptional}
                includeAutoData={includeAutoData}
                onComplaintChange={setComplaint}
                onExpectedBehaviorChange={setExpectedBehavior}
                onStepsToReproduceChange={setStepsToReproduce}
                onEnvironmentInfoChange={setEnvironmentInfo}
                onToggleOptional={() => setShowOptional(!showOptional)}
                onIncludeAutoDataChange={setIncludeAutoData}
                screenshot={screenshot}
                screenshotError={screenshotError}
                onRetakeScreenshot={async () => {
                  setScreenshotError(null);
                  const modal = document.querySelector('[role="dialog"]');
                  try {
                    if (modal) (modal as HTMLElement).style.visibility = 'hidden';
                    await new Promise((r) => setTimeout(r, 100));
                    const ss = await captureScreenshot();
                    setScreenshot(ss);
                  } catch (err) {
                    logger.warn('Failed to retake screenshot:', err);
                    setScreenshotError('スクリーンショットの再取得に失敗しました');
                  } finally {
                    if (modal) (modal as HTMLElement).style.visibility = 'visible';
                  }
                }}
              />
            ) : (
              <PreviewStep
                aiFormat={aiFormat}
                onBackToInput={() => {
                  aiFormat.reset();
                  setStep('input');
                }}
                onRetry={handleRequestFormat}
                onSubmitWithoutFormat={handleSubmitWithoutFormat}
              />
            )}

            {/* エラー表示 */}
            {submitError && <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">{submitError}</div>}
          </div>
        </div>

        <ModalFooter
          step={step}
          isCreating={isCreating}
          isFormatting={aiFormat.status === 'formatting'}
          canSubmitInput={!!complaint.trim()}
          disableSubmit={issueAlreadyCreated}
          onClose={handleClose}
          onRequestFormat={handleRequestFormat}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}

// --- ステップ1: 入力フォーム ---

function InputStep({
  complaint,
  expectedBehavior,
  stepsToReproduce,
  environmentInfo,
  showOptional,
  includeAutoData,
  onComplaintChange,
  onExpectedBehaviorChange,
  onStepsToReproduceChange,
  onEnvironmentInfoChange,
  onToggleOptional,
  onIncludeAutoDataChange,
  screenshot,
  screenshotError,
  onRetakeScreenshot,
}: {
  complaint: string;
  expectedBehavior: string;
  stepsToReproduce: string;
  environmentInfo: string;
  showOptional: boolean;
  includeAutoData: boolean;
  onComplaintChange: (v: string) => void;
  onExpectedBehaviorChange: (v: string) => void;
  onStepsToReproduceChange: (v: string) => void;
  onEnvironmentInfoChange: (v: string) => void;
  onToggleOptional: () => void;
  onIncludeAutoDataChange: (v: boolean) => void;
  screenshot: ScreenshotData | null;
  screenshotError: string | null;
  onRetakeScreenshot: () => void;
}) {
  return (
    <>
      {/* 必須: 何が嫌だったか */}
      <div className="space-y-2">
        <Label htmlFor="complaint">何が嫌だったか *</Label>
        <Textarea
          id="complaint"
          value={complaint}
          onChange={(e) => onComplaintChange(e.target.value)}
          placeholder="どんな問題が起きましたか？気軽に書いてください"
          rows={3}
        />
      </div>

      {/* オプション項目（折りたたみ） */}
      <button
        type="button"
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        onClick={onToggleOptional}
      >
        <ChevronDown className={`h-4 w-4 transition-transform ${showOptional ? 'rotate-180' : ''}`} />
        追加情報（オプション）
      </button>

      {showOptional && (
        <div className="space-y-4 pl-2 border-l-2 border-muted">
          <div className="space-y-2">
            <Label htmlFor="expectedBehavior">どうなって欲しかったか</Label>
            <Textarea
              id="expectedBehavior"
              value={expectedBehavior}
              onChange={(e) => onExpectedBehaviorChange(e.target.value)}
              placeholder="どうなるのが正しいと思いましたか？"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="stepsToReproduce">再現手順</Label>
            <Textarea
              id="stepsToReproduce"
              value={stepsToReproduce}
              onChange={(e) => onStepsToReproduceChange(e.target.value)}
              placeholder="どうすれば同じ問題が起きますか？"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="environmentInfo">環境情報</Label>
            <Textarea
              id="environmentInfo"
              value={environmentInfo}
              onChange={(e) => onEnvironmentInfoChange(e.target.value)}
              placeholder="ブラウザ、OS など"
              rows={1}
            />
          </div>
        </div>
      )}

      {/* スクリーンショット */}
      {screenshot && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">スクリーンショット</Label>
            <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onRetakeScreenshot}>
              再取得
            </Button>
          </div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          {/* biome-ignore lint/performance/noImgElement: Existing data URL preview cannot use next/image optimization. */}
          <img
            src={screenshot.dataUrl}
            alt="Screenshot preview"
            className="max-h-32 w-full object-contain rounded border"
          />
          {screenshotError && <p className="text-xs text-destructive">{screenshotError}</p>}
        </div>
      )}

      {/* 自動収集データ */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="includeAutoData"
          checked={includeAutoData}
          onChange={(e) => onIncludeAutoDataChange(e.target.checked)}
          className="rounded border-gray-300"
        />
        <Label htmlFor="includeAutoData" className="cursor-pointer text-sm">
          自動収集データを含める
        </Label>
      </div>
    </>
  );
}

// --- ステップ2: プレビュー ---

function PreviewStep({
  aiFormat,
  onBackToInput,
  onRetry,
  onSubmitWithoutFormat,
}: {
  aiFormat: ReturnType<typeof useAiFormat>;
  onBackToInput: () => void;
  onRetry: () => void;
  onSubmitWithoutFormat: () => void;
}) {
  if (aiFormat.status === 'formatting') {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">AI が内容を整形しています...</p>
      </div>
    );
  }

  if (aiFormat.status === 'error') {
    return (
      <div className="space-y-3">
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded">{aiFormat.error}</div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            <RotateCcw className="h-4 w-4 mr-1" />
            再試行する
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onSubmitWithoutFormat}>
            <ExternalLink className="h-4 w-4 mr-1" />
            整形せずに投稿する
          </Button>
        </div>
      </div>
    );
  }

  if (aiFormat.result) {
    return (
      <>
        <div className="flex items-center gap-2 mb-2">
          <Button type="button" variant="ghost" size="sm" onClick={onBackToInput}>
            <Undo2 className="h-4 w-4 mr-1" />
            元の入力に戻す
          </Button>
        </div>
        <AiFormattedPreview
          result={aiFormat.result}
          similarIssues={aiFormat.similarIssues}
          onUpdateTitle={aiFormat.updateTitle}
          onUpdateSection={aiFormat.updateSection}
        />
      </>
    );
  }

  return null;
}

// --- フッター ---

function ModalFooter({
  step,
  isCreating,
  isFormatting,
  canSubmitInput,
  disableSubmit,
  onClose,
  onRequestFormat,
  onSubmit,
}: {
  step: ModalStep;
  isCreating: boolean;
  isFormatting: boolean;
  canSubmitInput: boolean;
  disableSubmit: boolean;
  onClose: () => void;
  onRequestFormat: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="flex-shrink-0 flex justify-end gap-2 pt-4 border-t">
      <Button type="button" variant="outline" onClick={onClose} disabled={isCreating || isFormatting}>
        キャンセル
      </Button>
      {step === 'input' ? (
        <Button type="button" onClick={onRequestFormat} disabled={!canSubmitInput || isFormatting}>
          {isFormatting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              整形中...
            </>
          ) : (
            'AI で整形して確認'
          )}
        </Button>
      ) : (
        <Button type="button" onClick={onSubmit} disabled={isCreating || isFormatting || disableSubmit}>
          {isCreating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              作成中...
            </>
          ) : (
            <>
              <ExternalLink className="h-4 w-4 mr-2" />
              この内容で投稿する
            </>
          )}
        </Button>
      )}
    </div>
  );
}
